import { useEffect, useMemo, useState } from 'react';
import {
  Bouton, Carte, CaseACocher, EnTetePage, GrilleFormulaire, IconeTelecharger, Message,
  Selecteur, Squelette,
} from '@journal/ui';
import { useSession, messageErreur } from '../lib/session.tsx';
import { useDonnees } from '../lib/donnees.ts';
import { telechargerPdf } from '../lib/impression.ts';
import { classeChoisie } from '../components/SelecteurClasse.tsx';
import { pluriel, type ResumeSequence } from './fiches/modele.ts';
import {
  collecter, MODULES_IMPRIMABLES, type Annee, type CleModule,
} from './impressions/collecte.ts';
import { assemblerDossier, dateLongue, STYLE_DOSSIER, type Periode, type Section } from './impressions/sections.ts';

/**
 * Impressions — toutes les impressions de la classe, au même endroit.
 *
 * Chaque écran sait imprimer ce qu'il affiche : une journée, une matière sur
 * une période. Celui-ci imprime un DOSSIER : l'année ou une période, les
 * modules qu'on coche, une page de garde et un sommaire, chaque module sur une
 * page neuve et dans son orientation. C'est le classeur qu'on remet à
 * l'inspection, ou qu'on archive en fin d'année.
 *
 * ── Un PDF téléchargé, et non la boîte d'impression ─────────────────────────
 * Le dossier est un document qu'on archive ou qu'on transmet : il arrive en
 * PDF, rendu par le serveur, sans passer par « Enregistrer au format PDF » ni
 * par la date et l'adresse que le navigateur ajoute en marge des feuilles. Un
 * seul clic : collecte, mise en page, téléchargement — l'écran dit où il en
 * est.
 */

interface Classe {
  id: string;
  name: string;
  levels: string;
  teacherId: string | null;
  schoolYearId: string | null;
}

interface Matiere {
  id: string;
  name: string;
}


export function Impressions() {
  const { api } = useSession();
  const annees = useDonnees<Annee[]>('/school-years');
  const classes = useDonnees<Classe[]>('/classrooms');
  const moi = useDonnees<{ id: string; displayName: string; email: string }>('/auth/me');
  const reglages = useDonnees<Record<string, string>>('/settings');

  const [anneeId, setAnneeId] = useState('');
  const [classeId, setClasseId] = useState('');
  const [periodeId, setPeriodeId] = useState('');
  const [modules, setModules] = useState<Set<CleModule>>(
    () => new Set(MODULES_IMPRIMABLES.map((m) => m.cle).filter((c) => c !== 'journal')),
  );
  const [avecGarde, setAvecGarde] = useState(true);
  const [unJourParPage, setUnJourParPage] = useState(true);
  const [matieresRetenues, setMatieresRetenues] = useState<Set<string> | null>(null);
  const [sequencesRetenues, setSequencesRetenues] = useState<Set<string> | null>(null);

  const [etape, setEtape] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  /** Ce qui a été téléchargé en dernier : le compte des modules. */
  const [fait, setFait] = useState<Section[] | null>(null);

  // L'année active par défaut, puis la classe de travail si elle en est.
  useEffect(() => {
    if (anneeId || !annees.donnees?.length) return;
    setAnneeId((annees.donnees.find((a) => a.active) ?? annees.donnees[0]!).id);
  }, [annees.donnees, anneeId]);

  /*
   * Une classe sans année — créée avant qu'une année n'existe — est celle de
   * l'année en cours : c'est ainsi que le serveur la traite.
   */
  const anneeActiveId = annees.donnees?.find((a) => a.active)?.id;
  const classesDeLAnnee = useMemo(
    () => (classes.donnees ?? []).filter((c) => c.schoolYearId === anneeId
      || (c.schoolYearId === null && anneeId === anneeActiveId)),
    [classes.donnees, anneeId, anneeActiveId],
  );
  useEffect(() => {
    if (classesDeLAnnee.some((c) => c.id === classeId)) return;
    // La classe de travail du menu, sinon celle dont on est l'enseignant —
    // qu'on attend donc de connaître avant de choisir.
    const travail = classeChoisie();
    if (!travail && !moi.donnees) return;
    setClasseId((classesDeLAnnee.find((c) => c.id === travail)
      ?? classesDeLAnnee.find((c) => c.teacherId === moi.donnees?.id)
      ?? classesDeLAnnee[0])?.id ?? '');
  }, [classesDeLAnnee, classeId, moi.donnees?.id]);

  const periodes = useDonnees<Periode[]>(anneeId ? `/terms?schoolYearId=${anneeId}` : null, [anneeId]);
  const matieres = useDonnees<Matiere[]>('/subjects');
  // Les fiches appartiennent à une classe : la liste suit la classe choisie.
  const [sequences, setSequences] = useState<ResumeSequence[] | null>(null);
  useEffect(() => {
    if (!classeId) { setSequences([]); return; }
    let vivant = true;
    setSequences(null);
    api.requete<ResumeSequence[]>('/prep-sheets/sequences', { classe: classeId })
      .then((l) => { if (vivant) setSequences(l); })
      .catch(() => { if (vivant) setSequences([]); });
    return () => { vivant = false; };
  }, [api, classeId]);

  // Tout est coché tant que l'on n'a rien décoché.
  const nomsMatieres = (matieres.donnees ?? []).map((m) => m.name);
  const matieresCochees = matieresRetenues ?? new Set(nomsMatieres);
  const sequencesCochees = sequencesRetenues ?? new Set((sequences ?? []).map((s) => s.id));

  // Un réglage changé : le message du dernier téléchargement ne vaut plus.
  useEffect(() => { setFait(null); }, [
    anneeId, classeId, periodeId, modules, avecGarde, unJourParPage, matieresRetenues, sequencesRetenues,
  ]);

  const annee = annees.donnees?.find((a) => a.id === anneeId) ?? null;
  const classe = classesDeLAnnee.find((c) => c.id === classeId) ?? null;
  const periode = periodes.donnees?.find((p) => p.id === periodeId) ?? null;
  const avecMatieres = ['programmation', 'progressions', 'evaluation'].some((c) => modules.has(c as CleModule));

  function basculer<T>(ensemble: Set<T>, valeur: T, oui: boolean): Set<T> {
    const suivant = new Set(ensemble);
    if (oui) suivant.add(valeur); else suivant.delete(valeur);
    return suivant;
  }

  async function telecharger() {
    if (!annee || !classe) return;
    if (modules.size === 0) { setErreur('Cochez au moins un module.'); return; }
    setErreur(null);
    setFait(null);
    try {
      const ordre = MODULES_IMPRIMABLES.map((m) => m.cle).filter((c) => modules.has(c));
      const sections = await collecter(api, {
        annee,
        classeId: classe.id,
        periodeId: periode?.id ?? null,
        modules: ordre,
        matieres: [...matieresCochees],
        sequences: (sequences ?? []).map((s) => s.id).filter((id) => sequencesCochees.has(id)),
        unJourParPage,
      }, setEtape);
      const libellePeriode = periode
        ? `${periode.name || `Période ${periode.number}`} · du ${dateLongue(periode.startsOn).replace(/^\S+ /, '')} au ${dateLongue(periode.endsOn).replace(/^\S+ /, '')}`
        : 'Toute l’année';
      const corps = assemblerDossier({
        ecole: reglages.donnees?.['ecole'] ?? '',
        classe: classe.name,
        annee: annee.label,
        periode: libellePeriode,
        enseignant: moi.donnees?.displayName ?? '',
        imprimeLe: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }),
      }, sections, avecGarde);
      const titre = `Dossier de classe — ${classe.name} — ${annee.label}`
        + `${periode ? ` — ${periode.name || `Période ${periode.number}`}` : ''}`;
      setEtape('Mise en page du PDF');
      await telechargerPdf(api, { titre, corps, style: STYLE_DOSSIER, classe: classe.id });
      setFait(sections);
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEtape(null);
    }
  }

  const chargement = annees.chargement || classes.chargement;

  return (
    <div className="flex flex-col">
      <EnTetePage
        titre="Impressions"
        contexte="Toutes les impressions de la classe en un seul dossier : choisissez l’année, la période et les modules."
      />

      {(annees.erreur || classes.erreur) && (
        <div className="mb-4"><Message ton="erreur">{annees.erreur ?? classes.erreur}</Message></div>
      )}

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="flex flex-col gap-4">
          <Carte titre="Paramètres">
            {chargement ? <Squelette className="h-24" /> : (
              <GrilleFormulaire colonnes={3}>
                <Selecteur
                  libelle="Année scolaire"
                  value={anneeId}
                  options={(annees.donnees ?? []).map((a) => ({
                    valeur: a.id, texte: a.active ? `${a.label} (en cours)` : a.label,
                  }))}
                  onChange={(e) => { setAnneeId(e.target.value); setPeriodeId(''); setSequencesRetenues(null); }}
                />
                <Selecteur
                  libelle="Classe"
                  value={classeId}
                  disabled={classesDeLAnnee.length === 0}
                  aide={classesDeLAnnee.length === 0 ? 'Aucune classe pour cette année.' : undefined}
                  options={classesDeLAnnee.length
                    ? classesDeLAnnee.map((c) => ({ valeur: c.id, texte: c.levels ? `${c.name} (${c.levels})` : c.name }))
                    : [{ valeur: '', texte: '—' }]}
                  onChange={(e) => { setClasseId(e.target.value); setSequencesRetenues(null); }}
                />
                <Selecteur
                  libelle="Période"
                  value={periodeId}
                  options={[
                    { valeur: '', texte: 'Toute l’année' },
                    ...(periodes.donnees ?? []).map((p) => ({ valeur: p.id, texte: p.name || `Période ${p.number}` })),
                  ]}
                  onChange={(e) => setPeriodeId(e.target.value)}
                />
              </GrilleFormulaire>
            )}
          </Carte>

          <Carte
            titre="Modules"
            action={(
              <div className="flex gap-1">
                <Bouton variante="ghost" taille="sm" onClick={() => setModules(new Set(MODULES_IMPRIMABLES.map((m) => m.cle)))}>
                  Tout cocher
                </Bouton>
                <Bouton variante="ghost" taille="sm" onClick={() => setModules(new Set())}>
                  Tout décocher
                </Bouton>
              </div>
            )}
          >
            <ul className="flex flex-col gap-3">
              <li>
                <CaseACocher
                  libelle={<strong className="font-semibold">Page de garde et sommaire</strong>}
                  checked={avecGarde}
                  onChange={(e) => setAvecGarde(e.target.checked)}
                />
              </li>
              {MODULES_IMPRIMABLES.map((m) => (
                <li key={m.cle} className="flex flex-col gap-2">
                  <CaseACocher
                    libelle={<strong className="font-semibold">{m.titre}</strong>}
                    checked={modules.has(m.cle)}
                    onChange={(e) => setModules(basculer(modules, m.cle, e.target.checked))}
                  />
                  {m.cle === 'journal' && modules.has('journal') && (
                    <div className="ml-6.5">
                      <CaseACocher
                        libelle="Une journée par page"
                        checked={unJourParPage}
                        onChange={(e) => setUnJourParPage(e.target.checked)}
                      />
                    </div>
                  )}
                  {m.cle === 'fiches' && modules.has('fiches') && (
                    <div className="ml-6.5 flex flex-col gap-2 rounded-lg border border-[var(--color-border)] p-3">
                      {sequences === null ? <Squelette className="h-10" />
                        : sequences.length === 0 ? (
                          <p className="text-xs text-[var(--color-fg-muted)]">Aucune séquence dans cette classe.</p>
                        ) : sequences.map((s) => (
                          <CaseACocher
                            key={s.id}
                            libelle={s.title}
                            checked={sequencesCochees.has(s.id)}
                            onChange={(e) => setSequencesRetenues(basculer(sequencesCochees, s.id, e.target.checked))}
                          />
                        ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </Carte>

          {avecMatieres && (
            <Carte
              titre="Matières"
              description="Pour la programmation, les progressions et l’évaluation."
              action={(
                <div className="flex gap-1">
                  <Bouton variante="ghost" taille="sm" onClick={() => setMatieresRetenues(null)}>Toutes</Bouton>
                  <Bouton variante="ghost" taille="sm" onClick={() => setMatieresRetenues(new Set())}>Aucune</Bouton>
                </div>
              )}
            >
              <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {nomsMatieres.map((nom) => (
                  <li key={nom}>
                    <CaseACocher
                      libelle={nom}
                      checked={matieresCochees.has(nom)}
                      onChange={(e) => setMatieresRetenues(basculer(matieresCochees, nom, e.target.checked))}
                    />
                  </li>
                ))}
              </ul>
            </Carte>
          )}
        </div>

        {/* ── Le dossier ──────────────────────────────────────────────────── */}
        <aside className="flex flex-col gap-3 lg:sticky lg:top-4">
          <Carte titre="Dossier">
            <div className="flex flex-col gap-3 text-sm">
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                <dt className="text-[var(--color-fg-muted)]">Classe</dt><dd className="font-medium">{classe?.name ?? '—'}</dd>
                <dt className="text-[var(--color-fg-muted)]">Année</dt><dd className="font-medium">{annee?.label ?? '—'}</dd>
                <dt className="text-[var(--color-fg-muted)]">Période</dt>
                <dd className="font-medium">{periode ? (periode.name || `Période ${periode.number}`) : 'Toute l’année'}</dd>
                <dt className="text-[var(--color-fg-muted)]">Modules</dt><dd className="font-medium">{modules.size}</dd>
              </dl>

              {erreur && <Message ton="erreur">{erreur}</Message>}
              {etape && <p className="text-[var(--color-fg-muted)]" role="status">{`${etape}…`}</p>}
              {fait && (
                <Message ton="succes">
                  {`PDF téléchargé : ${pluriel(fait.length, 'module')}.`}
                </Message>
              )}

              <Bouton
                variante="primary"
                icone={<IconeTelecharger taille={15} />}
                chargement={!!etape}
                disabled={!annee || !classe || modules.size === 0}
                onClick={() => void telecharger()}
              >
                Télécharger le PDF
              </Bouton>
            </div>
          </Carte>
        </aside>
      </div>
    </div>
  );
}
